import { generatePath } from 'react-router-dom';
import { Bell, BellRing } from 'lucide-react';

import type { Site } from '@/api/fe-api/domain/entities/site';
import { Link } from '@/components/ui/link';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { StatsBox } from '@/modules/documents/view/components/pages/site-overview/stats-box';
import { label } from '@/utils/format/label';

export type CardNbAlertsProps = {
  site: Site;
  className?: string;
};

export function CardNbAlerts({ site, className }: CardNbAlertsProps) {
  const config = getConfig(site);

  return (
    <StatsBox
      className={className}
      icon={{ color: config.color, element: config.icon }}
      title="Alert Status"
      value={config.text}
    >
      <StatsBox.Action>
        <CardNbAlertsLink site={site} />
      </StatsBox.Action>
    </StatsBox>
  );
}

function CardNbAlertsLink({ site }: { site: Site }) {
  const featureFlag = useFeatureFlag();

  if (featureFlag('alerts')) {
    return (
      <Link
        to={generatePath('/asset/:siteId/alerts?', {
          siteId: site.id,
        })}
      >
        View Alerts
      </Link>
    );
  }

  if (featureFlag('taskmanager')) {
    return (
      <Link
        to={{
          pathname: '/tasks',
          search: new URLSearchParams({
            alert_status: 'Critical,Warning',
            group: site.fullFrontendId,
          }).toString(),
        }}
      >
        View Alert Tasks
      </Link>
    );
  }

  return null;
}

type CardConfig = {
  color: 'yellow' | 'red' | 'green' | 'neutral';
  text: string;
  icon: React.ReactElement<{ className?: string }>;
};

function getConfig(site: Site): CardConfig {
  if (site.alerts.critical > 0) {
    return {
      color: 'red',
      text: label(
        [
          `${site.alerts.critical} critical alert`,
          `${site.alerts.critical} critical alerts`,
        ],
        site.alerts.critical,
      ),
      icon: <BellRing />,
    };
  }

  if (site.alerts.warning > 0) {
    return {
      color: 'yellow',
      text: label(
        [`${site.alerts.warning} warning`, `${site.alerts.warning} warnings`],
        site.alerts.warning,
      ),
      icon: <BellRing />,
    };
  }

  return { color: 'neutral', text: 'No open alerts', icon: <Bell /> };
}
